import { useState } from 'react';
import { LogIn, User, LogOut, CreditCard, Sparkles } from 'lucide-react';
import { useEntitlements } from '@hooks/useEntitlements';

/**
 * SignInButton — shown in the TopBar.
 *
 * Two states:
 * - Signed out: "Sign in" button that redirects to the DDD auth hub
 * - Signed in: user avatar/initial with a dropdown menu (profile, sign out,
 *   and an "Upgrade to Host Pass" CTA if they don't have it)
 */
export default function SignInButton() {
  const {
    isSignedIn,
    displayName,
    photoURL,
    plan,
    hasHostPass,
    signIn,
    signOut,
    manageProfile,
    openPaywall,
  } = useEntitlements();
  const [menuOpen, setMenuOpen] = useState(false);

  // Signed-out state — simple Sign In button
  if (!isSignedIn) {
    return (
      <button
        onClick={signIn}
        title="Sign in with your DDD account"
        className="flex items-center gap-1.5 px-2.5 h-8 rounded-lg text-xs font-display font-semibold text-slate-500 hover:text-charcoal hover:bg-slate-100 transition-colors"
      >
        <LogIn className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Sign in</span>
      </button>
    );
  }

  // Signed-in state — avatar + dropdown
  const initial = (displayName || 'M')[0].toUpperCase();

  return (
    <div className="relative">
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        title={displayName || 'Account'}
        className="flex items-center gap-1.5 pl-1 pr-2 h-8 rounded-full hover:bg-slate-100 transition-colors"
      >
        {photoURL ? (
          <img
            src={photoURL}
            alt={displayName || 'Avatar'}
            className="w-6 h-6 rounded-full object-cover"
          />
        ) : (
          <div className="w-6 h-6 rounded-full bg-amber flex items-center justify-center text-[10px] font-bold text-white font-display">
            {initial}
          </div>
        )}
        {hasHostPass && (
          <Sparkles className="w-3 h-3 text-amber" />
        )}
      </button>

      {/* Dropdown menu */}
      {menuOpen && (
        <>
          {/* Backdrop — click outside closes */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute top-full right-0 mt-1 z-50 bg-white rounded-xl shadow-xl border border-slate-200 min-w-[220px] overflow-hidden">
            {/* User header */}
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-sm font-semibold text-charcoal font-display truncate">
                {displayName || 'Member'}
              </p>
              <p className="text-xs font-mono text-slate-400 mt-0.5">
                {hasHostPass ? (
                  <span className="text-amber flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5" />
                    Host Pass active
                  </span>
                ) : (
                  `Free tier${plan !== 'free' ? ` · ${plan}` : ''}`
                )}
              </p>
            </div>

            {/* Upgrade CTA (only if no Host Pass) */}
            {!hasHostPass && (
              <button
                onClick={() => {
                  setMenuOpen(false);
                  openPaywall('account-menu');
                }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-display text-charcoal hover:bg-amber/5 border-b border-slate-100"
              >
                <CreditCard className="w-4 h-4 text-amber" />
                <span>Get Host Pass</span>
              </button>
            )}

            {/* Profile */}
            <button
              onClick={() => {
                setMenuOpen(false);
                manageProfile();
              }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-display text-charcoal hover:bg-slate-50"
            >
              <User className="w-4 h-4 text-slate-400" />
              <span>Manage account</span>
            </button>

            {/* Sign out */}
            <button
              onClick={async () => {
                setMenuOpen(false);
                await signOut();
              }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-display text-charcoal hover:bg-slate-50 border-t border-slate-100"
            >
              <LogOut className="w-4 h-4 text-slate-400" />
              <span>Sign out</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
